import {isLoggedIn} from '@github-ui/client-env'
import {dismissUserNoticePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import type {CommitNotices, UserNotice} from '../types'

/**
 * Hook to get active (non-dismissed) user notices.
 *
 * @param userNotices - Array of user notices.
 * @returns Array of active user notices
 */
export function useUserNotices(userNotices?: Array<UserNotice<string>>) {
  const notices = userNotices ?? []

  return notices.filter(notice => !notice.dismissed)
}

/**
 * Hook to check if a specific notice has been dismissed.
 *
 * @param notice - The notice name to check
 * @param userNotices - Array of user notices.
 * @returns true if the notice is dismissed or not found, false otherwise
 */
export function useIsNoticeDismissed(notice: CommitNotices, userNotices?: Array<UserNotice<string>>) {
  const activeNotices = useUserNotices(userNotices)

  return activeNotices.filter(activeNotice => activeNotice.name === notice).length === 0
}

/**
 * Hook to dismiss a user notice.
 *
 * @param notice - The notice name to dismiss
 * @param userNotices - Array of user notices.
 * @returns Object containing dismissNotice function
 */
export function useDismissNotice(notice: CommitNotices, userNotices?: Array<UserNotice<string>>) {
  const isNoticedDimissed = useIsNoticeDismissed(notice, userNotices)

  const dismissNotice = useCallback(() => {
    // Don't dismiss the notice if the user is not logged in or the notice is already dismissed
    if (!isLoggedIn() || isNoticedDimissed) return

    verifiedFetch(dismissUserNoticePath({noticeName: notice}), {method: 'POST'})
  }, [isNoticedDimissed, notice])

  return {dismissNotice}
}
